import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { useFormatters } from '../../contexts/PreferencesContext'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
} from 'recharts'
import { SupplierRankingCard } from './SupplierRankingCard'
import { DrillDownModal, type DrillDownData } from './DrillDownModal'
import { TabProps, RechartsClickData } from './types'
import { useDrilldownAction, OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { ChartWrapper } from './ChartWrapper'
import { ArrearsBacklogChart } from './charts/ArrearsBacklogChart'
import { SectionTourTrigger } from '../tour/SectionTourTrigger'
import { useAuth } from '../../contexts/AuthContext'
import { useMenuSettings } from '../../contexts/MenuSettingsContext'
import { usePortalLabels } from '../../lib/portalLabels'
import { useQualityMeasureLabel } from '../../lib/qualityLabels'
import { KPIMetricsSection } from './KPIMetricsSection'
import { ScheduleCollaborationSection } from './ScheduleCollaborationSection'
import { calculateTrendLine, getPercentageDomain, parseMonthToDateRange } from './dashboardUtils'
import { formatCompactNumber } from '../../lib/utils'

/**
 * CR-4672: Custom tooltip for the OTD chart.
 * Reorders metrics to Early → Late → On-Time → OTD % → Target % → Trend %
 * and formats the three percentage series with 2 decimal places + "%" suffix.
 * Count-based series (Early / Late / On-Time) render as plain integers.
 *
 * CR-4673: The OTIF tooltip uses the same shape with its own ordering and
 * percentage set (see OTIFChartTooltip below).
 */
type ChartTooltipItem = {
  dataKey?: string
  name?: string
  value?: number | string | null
  color?: string
  payload?: Record<string, number | string | null | undefined>
}

function renderOrderedTooltip(
  active: boolean | undefined,
  payload: ChartTooltipItem[] | undefined,
  label: string | undefined,
  order: string[],
  displayNames: Record<string, string>,
  percentKeys: Set<string>,
) {
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const rows = order
    .map((key) => ({ key, item: byKey[key] }))
    .filter((row) => row.item !== undefined)

  const formatValue = (key: string, value: number | string | null | undefined): string => {
    if (value == null || value === '') return '-'
    const num = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(num)) return '-'
    if (percentKeys.has(key)) return `${num.toFixed(2)}%`
    return String(Math.round(num))
  }

  return (
    <div
      className="recharts-default-tooltip"
      style={{
        margin: 0,
        padding: 10,
        backgroundColor: 'rgb(255, 255, 255)',
        border: '1px solid rgb(204, 204, 204)',
        whiteSpace: 'nowrap',
      }}
    >
      {label && (
        <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>
      )}
      <ul
        className="recharts-tooltip-item-list"
        style={{ padding: 0, margin: 0, listStyle: 'none' }}
      >
        {rows.map(({ key, item }) => (
          <li
            key={key}
            className="recharts-tooltip-item"
            style={{
              display: 'block',
              paddingTop: 4,
              paddingBottom: 4,
              color: item!.color,
            }}
          >
            <span className="recharts-tooltip-item-name">{displayNames[key]}</span>
            <span className="recharts-tooltip-item-separator"> : </span>
            <span className="recharts-tooltip-item-value">
              {formatValue(key, item!.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function OTDChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string }) {
  const order = ['early', 'late', 'onTime', 'otd', 'target', 'otdTrend']
  const displayNames: Record<string, string> = {
    early: 'Early',
    late: 'Late',
    onTime: 'On-Time',
    otd: 'OTD %',
    target: 'Target %',
    otdTrend: 'Trend %',
  }
  const percentKeys = new Set(['otd', 'target', 'otdTrend'])
  return renderOrderedTooltip(active, payload, label, order, displayNames, percentKeys)
}

/**
 * CR-4673 / CR-4668 / CR-4709: Custom tooltip for the OTIF chart.
 * Shows count breakdown (Passed, Failed, Arrears, Total) then percentages.
 * Reads `totalLines` and `otif` from the backend payload directly so the
 * tooltip, the chart line and the drill-down all show the same numbers.
 */
function OTIFChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const toNum = (val: number | string | null | undefined): number => {
    if (val == null || val === '') return 0
    const n = typeof val === 'number' ? val : Number(val)
    return Number.isFinite(n) ? n : 0
  }

  const row = payload[0]?.payload
  const otifCount = toNum(byKey['otifCount']?.value)
  const failed = toNum(byKey['failed']?.value)
  const arrears = toNum(byKey['arrears']?.value)
  // CR-4709 round 6: backend now ships totalLines (Count('id') over every PO
  // line in the month, matching the drill-down's TOTAL LINES tab). Fall back
  // to passed+failed+arrears only if the field is missing on older data.
  const total = toNum(row?.['totalLines']) || (otifCount + failed + arrears)
  const otifFromBackend = row?.['otif']
  const otif = otifFromBackend != null && otifFromBackend !== '' ? toNum(otifFromBackend) : null
  const target = toNum(byKey['target']?.value)
  const trend = byKey['otifTrend']?.value

  type Row = { label: string; value: string; color?: string }
  const rows: Row[] = [
    { label: 'Passed OTIF PO Lines', value: String(Math.round(otifCount)), color: byKey['otifCount']?.color },
    { label: 'Failed OTIF PO Lines', value: String(Math.round(failed)), color: byKey['failed']?.color },
    { label: 'Arrears PO Lines', value: String(Math.round(arrears)), color: byKey['arrears']?.color },
    { label: 'Total PO Lines', value: String(Math.round(total)), color: '#6b7280' },
    { label: 'OTIF %', value: otif !== null ? `${otif.toFixed(2)}%` : '-', color: byKey['otif']?.color },
    { label: 'Target %', value: `${target.toFixed(2)}%`, color: byKey['target']?.color },
  ]
  if (trend != null && trend !== '') {
    rows.push({ label: 'Trend %', value: `${toNum(trend).toFixed(2)}%`, color: byKey['otifTrend']?.color })
  }

  return (
    <div
      className="recharts-default-tooltip"
      style={{ margin: 0, padding: 10, backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', whiteSpace: 'nowrap' }}
    >
      {label && <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>}
      <ul className="recharts-tooltip-item-list" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {rows.map((row) => (
          <li key={row.label} className="recharts-tooltip-item" style={{ display: 'block', paddingTop: 4, paddingBottom: 4, color: row.color }}>
            <span className="recharts-tooltip-item-name">{row.label}</span>
            <span className="recharts-tooltip-item-separator"> : </span>
            <span className="recharts-tooltip-item-value">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * CR-4674: Custom tooltip for the PPM chart.
 * Rounds values to integers with comma formatting (e.g. 79,985 not 79984.5)
 * and uses the configured quality measure label ("PPM" / "PPK").
 */
function PPMChartTooltip({ active, payload, label, measureLabel }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string; measureLabel: string }) {
  const order = ['ppm', 'target', 'ppmTrend']
  const displayNames: Record<string, string> = {
    ppm: measureLabel,
    target: 'Target',
    ppmTrend: 'Trend',
  }
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const rows = order
    .map((key) => ({ key, item: byKey[key] }))
    .filter((row) => row.item !== undefined)

  return (
    <div
      className="recharts-default-tooltip"
      style={{
        margin: 0,
        padding: 10,
        backgroundColor: 'rgb(255, 255, 255)',
        border: '1px solid rgb(204, 204, 204)',
        whiteSpace: 'nowrap',
      }}
    >
      {label && (
        <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>
      )}
      <ul
        className="recharts-tooltip-item-list"
        style={{ padding: 0, margin: 0, listStyle: 'none' }}
      >
        {rows.map(({ key, item }) => {
          const rawValue = item!.value
          const num = typeof rawValue === 'number' ? rawValue : Number(rawValue)
          const formatted = Number.isFinite(num) ? Math.round(num).toLocaleString() : '-'
          return (
            <li
              key={key}
              className="recharts-tooltip-item"
              style={{
                display: 'block',
                paddingTop: 4,
                paddingBottom: 4,
                color: item!.color,
              }}
            >
              <span className="recharts-tooltip-item-name">{displayNames[key]}</span>
              <span className="recharts-tooltip-item-separator"> : </span>
              <span className="recharts-tooltip-item-value">{formatted}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function DefaultTab({ data, currency, filters, dataPeriod: _dataPeriod, orderType: _orderType }: TabProps) {
  const { formatCurrency, preferences } = useFormatters()
  const { user } = useAuth()
  const { isFeatureEnabled } = useMenuSettings()
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration')
  const portalLabels = usePortalLabels(user?.platform)
  const qualityLabel = useQualityMeasureLabel()
  // Chart series keyed by the backend `ppm` metric field use the
  // configured short label ('PPM' / 'PPK') when shown to the user.
  const metricDisplayNames = useMemo<Record<string, string>>(
    () => ({ ppm: qualityLabel, ncr: 'NCR', otif: 'OTIF', otd: 'OTD' }),
    [qualityLabel],
  )

  const deliveryMode = data.delivery_metric_mode ?? 'both'
  // CR-4641 round 2: chart visibility is the AND of the company-wide
  // delivery_metric_mode admin override AND whether the metric has data
  // after the SupplierProfile flag filter (backend sends null when the
  // filtered queryset is empty).
  const showOTIF = (deliveryMode === 'both' || deliveryMode === 'otif_only') && data.quality.otif !== null
  const showOTD = (deliveryMode === 'both' || deliveryMode === 'otd_only') && data.delivery.otd !== null
  const showPPM = data.quality.ppm !== null

  // Convert dashboard filters to drilldown API format
  // CR-4886: include supplier_group so drilldowns scope the same way the
  // dashboard tile does (the Submitted Appeals popup was returning every
  // appeal regardless of the Supplier Group selection without this).
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
  }

  // CR-5557: human-readable label for the active timeframe so users can
  // validate what window the KPIs cover. Empty filters -> "All time".
  const formatPeriodLabel = (from?: string, to?: string): string => {
    if (!from && !to) return 'All time'
    const fmt = (s?: string) => {
      if (!s) return ''
      const d = new Date(s)
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    }
    if (from && to) return `${fmt(from)} – ${fmt(to)}`
    if (from) return `From ${fmt(from)}`
    return `Up to ${fmt(to)}`
  }
  const periodLabel = formatPeriodLabel(filters?.dateFrom, filters?.dateTo)

  // Calculate data with trend lines
  const spendDataWithTrend = calculateTrendLine(data.charts.spendByMonth || [], 'spend', 'trend')
  const otifDataWithTrend = calculateTrendLine(data.charts.otifData || [], 'otif', 'otifTrend')
  const otdDataWithTrend = calculateTrendLine(data.charts.otdData || [], 'otd', 'otdTrend')
  const ppmDataWithTrend = calculateTrendLine(data.charts.ppmTrend || [], 'ppm', 'ppmTrend')

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // Modal state for supplier drill-down
  const [drillDownData, setDrillDownData] = useState<DrillDownData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // OTIF specialized drill-down modal state
  const [isOTIFModalOpen, setIsOTIFModalOpen] = useState(false)
  const [otifModalContext, setOtifModalContext] = useState<string | undefined>(undefined)
  const [otifModalInitialTab, setOtifModalInitialTab] = useState<'failed' | 'arrears' | 'positive' | 'total' | undefined>(undefined)
  const [otifModalFilters, setOtifModalFilters] = useState<Record<string, string>>(drilldownFilters)

  // OTD specialized drill-down modal state
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState<string | undefined>(undefined)
  const [otdModalInitialTab, setOtdModalInitialTab] = useState<'on_time' | 'early' | 'late' | 'total' | undefined>(undefined)
  const [otdModalFilters, setOtdModalFilters] = useState<Record<string, string>>(drilldownFilters)

  // CR-3827: State for hover-to-highlight on chart legends
  const [hoveredOtifSeries, setHoveredOtifSeries] = useState<string | null>(null)
  const [hoveredOtdSeries, setHoveredOtdSeries] = useState<string | null>(null)
  const [hoveredPpmSeries, setHoveredPpmSeries] = useState<string | null>(null)
  const [hoveredSpendSeries, setHoveredSpendSeries] = useState<string | null>(null)

  // Format functions for supplier rankings
  const formatPPM = (value: number) => Math.round(value).toLocaleString()
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`
  const formatCurrencyValue = (value: number) => formatCurrency(value)

  // Custom tooltip formatter for currency values (portal-aware)
  const currencyTooltipFormatter = (value: number, name: string) => {
    const spendLabel = user?.platform === 'SUPPLIER' ? 'Revenue' : 'Spend'
    const arrearsLabel = user?.platform === 'SUPPLIER' ? 'Backlog Value' : 'Arrears Value'
    return [value.toLocaleString(), name === 'spend' ? spendLabel : name === 'value' ? arrearsLabel : name]
  }

  // Calculate domains for OTIF and OTD charts
  const otifDomain = getPercentageDomain(otifDataWithTrend, 'otif')
  const otdDomain = getPercentageDomain(otdDataWithTrend, 'otd')

  // Handle supplier click - open drill-down modal
  const handleSupplierClick = (supplierId: number, metricType: 'ppm' | 'otif' | 'otd' | 'arrears') => {
    let rankings
    let target

    switch (metricType) {
      case 'ppm':
        rankings = data.supplierRankings?.worstPPM || []; target = 1500; break
      case 'otif':
        rankings = data.supplierRankings?.worstOTIF || []; target = 88; break
      case 'otd':
        rankings = data.supplierRankings?.worstOTD || []; target = 90; break
      case 'arrears':
        rankings = data.supplierRankings?.highestArrears || []; target = undefined; break
    }

    const supplierData = rankings.find(s => s.supplier_id === supplierId)

    if (supplierData) {
      setDrillDownData({
        supplier_id: supplierData.supplier_id,
        supplier_name: supplierData.supplier_name,
        metric_type: metricType,
        current_value: supplierData.value,
        previous_value: supplierData.trend === 'stable' ? supplierData.value : undefined,
        target: supplierData.target || target,
        details: undefined,
        summary: { total_pos: undefined, total_lines: undefined, total_value: undefined, affected_parts: undefined }
      })
      setIsModalOpen(true)
    }
  }

  // Handle chart click - open drill-down modal with month-specific data using API
  const handleChartClick = (chartData: RechartsClickData | null, metricType: 'ppm' | 'otif' | 'otd' | 'spend' | 'arrears' | 'ncrs') => {
    if (!chartData) return

    let month: string | null = null
    if (chartData.activePayload && chartData.activePayload[0]) {
      month = (chartData.activePayload[0].payload?.['month'] as string) || null
    } else if (chartData.activeLabel) {
      month = chartData.activeLabel
    }
    if (!month) return

    const dateRange = parseMonthToDateRange(month)
    if (!dateRange) return

    const chartFilters = { ...drilldownFilters, date_from: dateRange.date_from, date_to: dateRange.date_to }

    // CR-4785: PPM clicks route through the shared openDrilldown flow, same
    // as the top-of-dashboard PPM KPI (which goes through DrilldownTrigger).
    // Previously this branch opened a stub PPMDrilldownModal that just said
    // "coming soon" — two entry points with divergent behaviour. Falling
    // through to the shared metricKeyMap handling below keeps future
    // changes (columns, filters, layout) applied to both entry points.

    // For OTIF, use the specialized OTIF drill-down modal
    if (metricType === 'otif') {
      let initialTab: 'failed' | 'arrears' | 'positive' | 'total' | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'failed') { initialTab = 'failed'; break }
          else if (item.dataKey === 'arrears') { initialTab = 'arrears'; break }
          else if (item.dataKey === 'otifCount') { initialTab = 'positive'; break }
        }
      }
      setOtifModalFilters(chartFilters); setOtifModalContext(month); setOtifModalInitialTab(initialTab); setIsOTIFModalOpen(true); return
    }

    // For OTD, use the specialized OTD drill-down modal
    if (metricType === 'otd') {
      let initialTab: 'on_time' | 'early' | 'late' | 'total' | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'onTime') { initialTab = 'on_time'; break }
          else if (item.dataKey === 'early') { initialTab = 'early'; break }
          else if (item.dataKey === 'late') { initialTab = 'late'; break }
        }
      }
      setOtdModalFilters(chartFilters); setOtdModalContext(month); setOtdModalInitialTab(initialTab); setIsOTDModalOpen(true); return
    }

    const metricKeyMap: Record<string, string> = { 'ppm': 'ppm', 'spend': 'spend', 'arrears': 'arrears', 'ncrs': 'ncr' }
    const metricKey = metricKeyMap[metricType]
    if (!metricKey) return
    openDrilldown(metricKey, chartFilters, month)
  }

  // Handle arrears backlog bar click — converts week number to a date range for drill-down
  const handleArrearsBacklogClick = (barData: { week: string; count: number; value: number }) => {
    const weekLabel = barData.week
    const weekNum = weekLabel === '12+' ? 12 : parseInt(weekLabel, 10)
    if (isNaN(weekNum)) return

    const today = new Date()
    // Week N means delivery_date was (N-1)*7 to N*7 days ago (12+ means 12*7+ days ago)
    const dateTo = new Date(today)
    dateTo.setDate(today.getDate() - (weekNum - 1) * 7)
    const dateFrom = weekLabel === '12+' ? undefined : new Date(today)
    if (dateFrom) dateFrom.setDate(today.getDate() - weekNum * 7)

    const chartFilters: Record<string, string> = {
      ...drilldownFilters,
      date_to: dateTo.toISOString().split('T')[0],
    }
    if (dateFrom) chartFilters['date_from'] = dateFrom.toISOString().split('T')[0]
    openDrilldown('arrears', chartFilters, `Week ${weekLabel}`)
  }

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4"></div>
        <SectionTourTrigger pageKey="dashboard" sectionKey="default" variant="link" label="Tour this tab" />
      </div>

      {/* KPI Metrics Section */}
      <KPIMetricsSection
        data={data}
        currency={currency}
        drilldownFilters={drilldownFilters}
        periodLabel={periodLabel}
        onOTIFClick={() => { setOtifModalFilters(drilldownFilters); setOtifModalContext(periodLabel); setOtifModalInitialTab('total'); setIsOTIFModalOpen(true) }}
        onOTDClick={() => { setOtdModalFilters(drilldownFilters); setOtdModalContext(periodLabel); setOtdModalInitialTab('total'); setIsOTDModalOpen(true) }}
      />

      {/* Supplier Rankings - Actionable Insights */}
      {data.supplierRankings && (
        <div data-tour="supplier-rankings">
          <h2 className="text-lg font-semibold mb-3 px-1">{portalLabels.suppliersRequiringAttention}</h2>
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${2 + (showOTIF ? 1 : 0) + (showOTD ? 1 : 0)} gap-4`}>
            <SupplierRankingCard title={portalLabels.worstQuality} suppliers={data.supplierRankings.worstPPM || []} metricType="ppm" formatValue={formatPPM} inverse={true} onSupplierClick={(id) => handleSupplierClick(id, 'ppm')} />
            {showOTIF && <SupplierRankingCard title={portalLabels.worstOTIF} suppliers={data.supplierRankings.worstOTIF || []} metricType="otif" formatValue={formatPercentage} inverse={false} onSupplierClick={(id) => handleSupplierClick(id, 'otif')} />}
            {showOTD && <SupplierRankingCard title={portalLabels.worstOTD} suppliers={data.supplierRankings.worstOTD || []} metricType="otd" formatValue={formatPercentage} inverse={false} onSupplierClick={(id) => handleSupplierClick(id, 'otd')} />}
            <SupplierRankingCard title={portalLabels.highestArrears} suppliers={data.supplierRankings.highestArrears || []} metricType="arrears" formatValue={formatCurrencyValue} inverse={true} onSupplierClick={(id) => handleSupplierClick(id, 'arrears')} />
          </div>
        </div>
      )}

      {/* First Row Charts — CR-4641 round 2: hide row entirely when both
          PPM and OTIF are excluded; show one column if only one is visible. */}
      {(showPPM || showOTIF) && (
      <div className={`grid gap-4 ${showPPM && showOTIF ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* PPM Chart */}
        {showPPM && <div data-tour="ppm-chart">
        <ChartWrapper title={qualityLabel} data={ppmDataWithTrend} exportFilename="ppm-trend"
          legend={[{ color: '#3b82f6', label: metricDisplayNames['ppm']!, dataKey: 'ppm' }, { color: '#ef4444', label: 'Target', dataKey: 'target' }, { color: '#f97316', label: 'Trend', dataKey: 'ppmTrend' }]}
          hoveredSeries={hoveredPpmSeries} onSeriesHover={setHoveredPpmSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={ppmDataWithTrend} onClick={(chartData) => handleChartClick(chartData, 'ppm')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              {/* CR-4537: interval={0} forces every month tick to render (recharts default culls end labels on long ranges e.g. Jan 23 – Oct 25); angle + smaller font keep them readable when dense */}
              <XAxis dataKey="month" tick={{ fontSize: ppmDataWithTrend.length > 24 ? 9 : 11 }} interval={0} angle={ppmDataWithTrend.length > 12 ? -45 : 0} textAnchor={ppmDataWithTrend.length > 12 ? 'end' : 'middle'} height={ppmDataWithTrend.length > 12 ? 55 : 30} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<PPMChartTooltip measureLabel={qualityLabel} />} />
              <Line type="monotone" dataKey="ppm" stroke="#3b82f6" strokeWidth={hoveredPpmSeries === 'ppm' ? 3 : 2} strokeOpacity={hoveredPpmSeries && hoveredPpmSeries !== 'ppm' ? 0.2 : 1} dot={{ r: hoveredPpmSeries === 'ppm' ? 4 : 3 }} />
              <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={hoveredPpmSeries === 'target' ? 3 : 2} strokeOpacity={hoveredPpmSeries && hoveredPpmSeries !== 'target' ? 0.2 : 1} strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="ppmTrend" stroke="#f97316" strokeWidth={hoveredPpmSeries === 'ppmTrend' ? 3 : 2} strokeOpacity={hoveredPpmSeries && hoveredPpmSeries !== 'ppmTrend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
              {ppmDataWithTrend.length > 0 && <ReferenceLine x={ppmDataWithTrend[0]?.month} yAxisId={0} stroke="#9333ea" strokeWidth={2} strokeDasharray="4 4" label={{ value: 'Launch', position: 'top', fill: '#9333ea', fontSize: 10 }} />}
              {ppmDataWithTrend.length > 2 && <ReferenceLine x={ppmDataWithTrend[2]?.month} yAxisId={0} stroke="#0ea5e9" strokeWidth={2} strokeDasharray="4 4" label={{ value: 'Supplier Onboarded', position: 'top', fill: '#0ea5e9', fontSize: 10 }} />}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>
        </div>}

        {/* OTIF Chart */}
        {showOTIF && <div data-tour="otif-chart">
        <ChartWrapper title="OTIF" data={otifDataWithTrend} exportFilename="otif-trend"
          legend={[{ color: '#1d4ed8', label: 'OTIF %', dataKey: 'otif' }, { color: '#dc2626', label: 'Target', dataKey: 'target' }, { color: '#f97316', label: 'Trend', dataKey: 'otifTrend' }, { color: '#10b981', label: 'OTIF PO Lines', dataKey: 'otifCount' }, { color: '#fbbf24', label: 'Arrears PO Lines', dataKey: 'arrears' }, { color: '#ef4444', label: 'Failed OTIF PO Lines', dataKey: 'failed' }]}
          hoveredSeries={hoveredOtifSeries} onSeriesHover={setHoveredOtifSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={otifDataWithTrend} onClick={(chartData) => handleChartClick(chartData, 'otif')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={otifDomain} tickFormatter={(v) => `${v}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<OTIFChartTooltip />} />
              {otifDataWithTrend.length > 0 && <ReferenceLine x={otifDataWithTrend[0]?.month} yAxisId="left" stroke="#9333ea" strokeWidth={2} strokeDasharray="4 4" label={{ value: 'Launch', position: 'top', fill: '#9333ea', fontSize: 10 }} />}
              {otifDataWithTrend.length > 2 && <ReferenceLine x={otifDataWithTrend[2]?.month} yAxisId="left" stroke="#0ea5e9" strokeWidth={2} strokeDasharray="4 4" label={{ value: 'Supplier Onboarded', position: 'top', fill: '#0ea5e9', fontSize: 10 }} />}
              <Bar yAxisId="right" dataKey="otifCount" stackId="a" fill="#10b981" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifCount' ? 0.2 : 1} name="OTIF PO Lines" />
              <Bar yAxisId="right" dataKey="arrears" stackId="a" fill="#fbbf24" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'arrears' ? 0.2 : 1} name="Arrears PO Lines" />
              <Bar yAxisId="right" dataKey="failed" stackId="a" fill="#ef4444" fillOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'failed' ? 0.2 : 1} name="Failed OTIF PO Lines" />
              <Line yAxisId="left" type="monotone" dataKey="otif" stroke="#1d4ed8" strokeWidth={hoveredOtifSeries === 'otif' ? 4 : 3} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otif' ? 0.2 : 1} dot={{ r: hoveredOtifSeries === 'otif' ? 8 : 6, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTIF %" />
              <Line yAxisId="left" type="monotone" dataKey="target" stroke="#dc2626" strokeWidth={hoveredOtifSeries === 'target' ? 4 : 3} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'target' ? 0.2 : 1} strokeDasharray="8 4" dot={false} name="Target" />
              <Line yAxisId="left" type="monotone" dataKey="otifTrend" stroke="#f97316" strokeWidth={hoveredOtifSeries === 'otifTrend' ? 3 : 2} strokeOpacity={hoveredOtifSeries && hoveredOtifSeries !== 'otifTrend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>
        </div>}
      </div>
      )}

      {/* Second Row Charts */}
      <div className={`grid gap-4 ${showOTD ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* Spend by Month */}
        <ChartWrapper title={`${portalLabels.spendByMonth} (${preferences.currency})`} data={spendDataWithTrend} exportFilename="spend-by-month"
          legend={[{ color: '#3b82f6', label: 'Spend', dataKey: 'spend' }, { color: '#f97316', label: 'Trend', dataKey: 'trend' }]}
          hoveredSeries={hoveredSpendSeries} onSeriesHover={setHoveredSpendSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={spendDataWithTrend} onClick={(chartData) => handleChartClick(chartData, 'spend')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              {/* CR-4537: interval={0} forces every month tick to render; angle + smaller font keep them readable when dense */}
              <XAxis dataKey="month" tick={{ fontSize: spendDataWithTrend.length > 24 ? 9 : 11 }} interval={0} angle={spendDataWithTrend.length > 12 ? -45 : 0} textAnchor={spendDataWithTrend.length > 12 ? 'end' : 'middle'} height={spendDataWithTrend.length > 12 ? 55 : 30} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={currencyTooltipFormatter} />
              <Area type="monotone" dataKey="spend" stroke="#3b82f6" fill="#93c5fd" fillOpacity={hoveredSpendSeries && hoveredSpendSeries !== 'spend' ? 0.2 : 0.8} strokeOpacity={hoveredSpendSeries && hoveredSpendSeries !== 'spend' ? 0.2 : 1} strokeWidth={hoveredSpendSeries === 'spend' ? 3 : 2} name="Spend" />
              <Line type="monotone" dataKey="trend" stroke="#f97316" strokeWidth={hoveredSpendSeries === 'trend' ? 3 : 2} strokeOpacity={hoveredSpendSeries && hoveredSpendSeries !== 'trend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>

        {/* OTD Chart */}
        {showOTD && <ChartWrapper title="OTD" data={otdDataWithTrend} exportFilename="otd-trend"
          legend={[{ color: '#1d4ed8', label: 'OTD %', dataKey: 'otd' }, { color: '#dc2626', label: 'Target', dataKey: 'target' }, { color: '#a855f7', label: 'Trend', dataKey: 'otdTrend' }, { color: '#10b981', label: 'On Time', dataKey: 'onTime' }, { color: '#fbbf24', label: 'Early', dataKey: 'early' }, { color: '#f97316', label: 'Late', dataKey: 'late' }]}
          hoveredSeries={hoveredOtdSeries} onSeriesHover={setHoveredOtdSeries}>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={otdDataWithTrend} onClick={(chartData) => handleChartClick(chartData, 'otd')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} domain={otdDomain} allowDataOverflow={true} tickFormatter={(v) => `${v}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              {/* CR-4672: Custom tooltip — reorders series and formats %s with 2dp */}
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} content={<OTDChartTooltip />} />
              <Bar yAxisId="right" dataKey="onTime" stackId="a" fill="#10b981" fillOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'onTime' ? 0.2 : 1} name="On-Time" />
              <Bar yAxisId="right" dataKey="early" stackId="a" fill="#fbbf24" fillOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'early' ? 0.2 : 1} name="Early" />
              <Bar yAxisId="right" dataKey="late" stackId="a" fill="#f97316" fillOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'late' ? 0.2 : 1} name="Late" />
              <Line yAxisId="left" type="monotone" dataKey="otd" stroke="#1d4ed8" strokeWidth={hoveredOtdSeries === 'otd' ? 4 : 3} strokeOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'otd' ? 0.2 : 1} dot={{ r: hoveredOtdSeries === 'otd' ? 8 : 6, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTD %" />
              <Line yAxisId="left" type="monotone" dataKey="target" stroke="#dc2626" strokeWidth={hoveredOtdSeries === 'target' ? 4 : 3} strokeOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'target' ? 0.2 : 1} strokeDasharray="8 4" dot={false} name="Target" />
              <Line yAxisId="left" type="monotone" dataKey="otdTrend" stroke="#a855f7" strokeWidth={hoveredOtdSeries === 'otdTrend' ? 3 : 2} strokeOpacity={hoveredOtdSeries && hoveredOtdSeries !== 'otdTrend' ? 0.2 : 1} strokeDasharray="5 5" dot={false} name="Trend" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrapper>}
      </div>

      {/* Third Row - Arrears Backlog (weekly age buckets: Week 1..12+) */}
      <ChartWrapper title="Arrears Backlog (Weeks Overdue)" exportFilename="arrears-backlog"
        legend={[{ color: '#10b981', label: 'Low' }, { color: '#eab308', label: 'Medium' }, { color: '#ef4444', label: 'High' }]}>
        <ArrearsBacklogChart
          data={data.charts.arrearsBacklog?.map(d => ({ week: d.week, count: d.count || 0, value: d.value })) || []}
          onBarClick={handleArrearsBacklogClick}
          currency={preferences.currency}
        />
      </ChartWrapper>

      {/* Fourth Row Charts - Historical Arrears Split */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper title={`Historical Arrears by Value (${preferences.currency})`} data={data.charts.historicalArrears} exportFilename="historical-arrears-value"
          legend={[{ color: '#ef4444', label: 'Arrears Value' }]}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.charts.historicalArrears} onClick={(chartData) => handleChartClick(chartData, 'arrears')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={currencyTooltipFormatter} />
              <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Arrears Value" />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper title="Historical Arrears (Count)" data={data.charts.historicalArrears?.map(d => ({ ...d, count: d.count || 0 }))} exportFilename="historical-arrears-count"
          legend={[{ color: '#f97316', label: 'PO Lines' }]}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.charts.historicalArrears?.map(d => ({ ...d, count: d.count || 0 }))} onClick={(chartData) => handleChartClick(chartData, 'arrears')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} name="PO Lines in Arrears" />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Fifth Row Charts */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper title="Live NCRs Overdue" data={data.charts.liveNCRsOverdue} exportFilename="live-ncrs-overdue"
          legend={[{ color: '#ef4444', label: 'Overdue' }, { color: '#fbbf24', label: 'Pending' }]}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.charts.liveNCRsOverdue} onClick={(chartData) => handleChartClick(chartData, 'ncrs')} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="overdue" stackId="ncrs" fill="#ef4444" name="Overdue" />
              <Bar dataKey="pending" stackId="ncrs" fill="#fbbf24" name="Pending" />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Performance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 h-[200px] items-center">
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Total PO Lines</p>
                <p className="text-2xl font-bold">{data.purchasing.totalLines.toLocaleString()}</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Total NCRs</p>
                <p className="text-2xl font-bold">{data.quality.ncrs.toLocaleString()}</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Active Suppliers</p>
                <p className="text-2xl font-bold">{data.suppliers.active.toLocaleString()}</p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Open POs</p>
                <p className="text-2xl font-bold">{data.purchasing.openPOs.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">All orders</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule Collaboration (DE feature flag) */}
      {scheduleCollabEnabled && <ScheduleCollaborationSection />}

      {/* Modals */}
      {drillDownData && <DrillDownModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data={drillDownData} currency={currency.currentCurrency} />}
      <OTIFDrilldownModal isOpen={isOTIFModalOpen} onClose={() => setIsOTIFModalOpen(false)} filters={otifModalFilters} contextLabel={otifModalContext} initialTab={otifModalInitialTab} />
      <OTDDrilldownModal isOpen={isOTDModalOpen} onClose={() => setIsOTDModalOpen(false)} filters={otdModalFilters} contextLabel={otdModalContext} initialTab={otdModalInitialTab} />
    </div>
  )
}
